import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import Heading from "./Heading";
import Footer from "./Footer";
import { Button, CardActions, Container, LinearProgress, Stack, ThemeProvider, Typography, } from "@mui/material";
import getTheme from "./Theme";
import { ContentCardStacks } from "./components/ContentCardStacks";
import { loadFrontPageDiscovery } from "./store/DiscoveryStore";
import { useNavigate } from "react-router-dom";
import { restoreSession, useProfileAuthStore } from "./store/ProfileStore";
import { GuideCard } from "./components/GuideCard";
import { useCacheBusterStore } from "./util/UrlUtils";
import { ContentCard } from "./components/ContentCard";
import { useLogger } from "./logger";
import { TopButtonMenu } from "./components/TopButtonMenu";
import { NewPostCard } from "./components/NewPostCard";
import { resetCache } from "./store/CommentsStore";
import { useAgeVerificationGating } from "./hooks/useAgeVerificationGating";
const LOGPREFIX = "Latest: ";
export function Latest() {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [discoveryCards, setDiscoveryCards] = useState([]);
    const [allDiscoveryCards, setAllDiscoveryCards] = useState([]);
    const [removedPostKeys, setRemovedPostKeys] = useState(new Set());
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const auth = useProfileAuthStore((state) => state);
    const ageVerification = useAgeVerificationGating();
    const navigate = useNavigate();
    const logger = useLogger((state) => state);
    const handlePostLoadError = (postId, error) => {
        logger.log(LOGPREFIX + "handlePostLoadError: postId=" + postId + ", error=" + error);
        // Add the postId to removed set to filter it out
        setRemovedPostKeys((prev) => new Set(prev).add(postId));
    };
    useEffect(() => {
        if (!auth.isSignedIn(logger)) {
            restoreSession(auth, logger);
        }
        else {
            if (auth &&
                auth.isSignedIn() &&
                auth.bearer &&
                auth.sequence &&
                !loading &&
                !loaded) {
                setLoading(true);
                loadFrontPageDiscovery({
                    okCallback: (elements) => {
                        setAllDiscoveryCards(elements);
                        setLoading(false);
                        setLoaded(true);
                    },
                    errorCallback: (errorMessage) => {
                        logger.error(LOGPREFIX +
                            "Latest.useEffect.discovery load failed: " +
                            errorMessage);
                        setErrorMessage(errorMessage);
                        setLoading(false);
                        setLoaded(true);
                    },
                }, false, navigate, cacheBusterStore, logger, auth, handlePostLoadError);
            }
        }
        return () => { };
    }, [auth.isSignedIn(), auth.userId, loaded]);
    // Filter discovery cards based on age verification status and removed posts
    useEffect(() => {
        if (allDiscoveryCards.length > 0 && !ageVerification.isLoading) {
            logger.log(LOGPREFIX +
                "Filtering discovery cards based on age verification status and removed posts");
            // Filter out cards that have been marked as removed (deleted posts)
            const filteredCards = allDiscoveryCards.filter((card) => {
                const cardKey = card.key;
                // Extract postId from card key (format: "postKeyCard.channelOwnerId:channelId:postId")
                if (cardKey && cardKey.startsWith("postKeyCard.")) {
                    const postKey = cardKey.replace("postKeyCard.", "");
                    const parts = postKey.split(":");
                    if (parts.length >= 3) {
                        const postId = parts[2];
                        const isRemoved = removedPostKeys.has(postId);
                        if (isRemoved) {
                            logger.log(LOGPREFIX + "Filtering out removed post: " + postId);
                        }
                        return !isRemoved;
                    }
                }
                return true; // Keep non-post cards (like channel cards)
            });
            setDiscoveryCards(filteredCards);
            if (!ageVerification.isVerified) {
                logger.log(LOGPREFIX +
                    "User is not age verified - adult content posts may be hidden server-side");
                // Note: Proper filtering should be implemented in the discovery lambda
                // to check channel permissions and user age verification status
            }
        }
    }, [
        allDiscoveryCards,
        ageVerification.isVerified,
        ageVerification.isLoading,
        removedPostKeys,
    ]);
    const cardsList = [];
    if (errorMessage) {
        cardsList.push(_jsx(ContentCard, { title: "ERROR", keyStr: "error.card.latest", textColor: "#000000", backgroundColor: "#FFEEEE", childComponent: _jsx(Typography, Object.assign({ variant: "body1" }, { children: errorMessage })), actions: _jsx(CardActions, Object.assign({ sx: { display: "flex", justifyContent: "center" } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: () => {
                        setErrorMessage(undefined);
                        setLoaded(false);
                    } }, { children: "RETRY" })) })) }, "error.card.latest"));
    }
    else if (loaded) {
        if (auth.userId) {
            cardsList.push(_jsx(NewPostCard, { channelOwnerId: auth.userId, channelId: auth.userId, keyStr: "new.post.card", auth: auth, reload: function () {
                    setTimeout(() => {
                        resetCache();
                        setLoaded(false);
                    }, 300);
                } }, "new.post.card"));
        }
        discoveryCards.forEach((card) => {
            cardsList.push(card);
        });
        if (!loading) {
            cardsList.push(_jsx(GuideCard, { keyStr: "latest.guidecard.1" }, "guide.card.1"));
        }
    }
    const cardsStack = (_jsx(ContentCardStacks, { keyStr: "cards.stack.latest", cards: cardsList, columnCount: 4 }, "cards.stack.latest"));
    const buttons = _jsx(TopButtonMenu, { current: "latest" });
    const output = (_jsx("div", Object.assign({ style: { width: "100%", height: "100%", padding: 0, margin: 0 }, id: "outer" }, { children: _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: {
                        backgroundColor: "#FFFFFF",
                        width: "100%",
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        justifyContent: "center",
                    }, maxWidth: false }, { children: _jsxs(Stack, Object.assign({ direction: "column", sx: { display: "flex", justifyContent: "center", width: "100%" } }, { children: [buttons, loading && _jsx(LinearProgress, { sx: { margin: "2px" } }), cardsStack] })) })), _jsx(Footer, { stickToBottom: false })] })) })));
    return output;
}
